import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createMyDailyDirectClient, type MyDailyDirectClient } from "@mydailydirect/api-client";
import { createAuthHeaderProvider, gatewayConfigFromEnv, type GatewayConfig } from "./gateway";
import {
  clearSession,
  loadSession,
  saveSession,
  registerUserRecord,
  findUser,
  type Session,
  type UserRecord,
} from "./session";

interface AuthContextValue {
  session: Session | null;
  client: MyDailyDirectClient;
  login: (session: Session) => void;
  logout: () => void;
  register: (userData: Omit<UserRecord, "createdAt">) => Session;
  findUser: (userIdOrPhone: string) => UserRecord | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Gateway config; defaults to values derived from `import.meta.env`. */
  gatewayConfig?: GatewayConfig;
  /** Optional API base URL override (defaults to same-origin proxy path). */
  baseUrl?: string;
}

export function AuthProvider({ children, gatewayConfig, baseUrl }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(() => loadSession());

  // The header provider reads the latest session via a ref so a single client instance stays valid
  // across login/logout without being recreated (which would drop React Query caches unexpectedly).
  const sessionRef = useRef<Session | null>(session);
  sessionRef.current = session;

  const config = useMemo(
    () => gatewayConfig ?? gatewayConfigFromEnv(readEnv()),
    [gatewayConfig],
  );

  const client = useMemo(() => {
    const provider = createAuthHeaderProvider(() => sessionRef.current, config);
    return createMyDailyDirectClient({ baseUrl, getAuthHeaders: provider });
  }, [config, baseUrl]);

  const login = useCallback((next: Session) => {
    saveSession(next);
    if (typeof window !== "undefined" && window.location.pathname !== "/") {
      window.history.pushState(null, "", "/");
    }
    setSession(next);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    if (typeof window !== "undefined" && window.location.pathname !== "/") {
      window.history.pushState(null, "", "/");
    }
    setSession(null);
  }, []);

  const register = useCallback((userData: Omit<UserRecord, "createdAt">): Session => {
    const record = registerUserRecord(userData);
    const phone = record.phone || record.phoneNumber;
    const orgId = record.orgId || record.organizationId || "mydailydirect";
    const newSession: Session = {
      tenantId: "mydailydirect",
      userId: record.userId,
      fullName: record.fullName,
      roles: record.roles,
      phone: phone,
      phoneNumber: phone,
      orgId: orgId,
      organizationId: orgId,
      businessName: record.businessName,
      accountType: record.accountType,
      createdAt: record.createdAt,
    };
    saveSession(newSession);
    if (typeof window !== "undefined" && window.location.pathname !== "/") {
      window.history.pushState(null, "", "/");
    }
    setSession(newSession);
    return newSession;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, client, login, logout, register, findUser }),
    [session, client, login, logout, register],
  );

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

/** Convenience hook returning the typed API client bound to the current session. */
export function useMyDailyDirectClient(): MyDailyDirectClient {
  return useAuth().client;
}

export const useTradoClient = useMyDailyDirectClient;

function readEnv(): Record<string, string | undefined> {
  try {
    return (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  } catch {
    return {};
  }
}
